import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { SelectOption } from "../CreateBranch/types";
import { Button, Col, Row } from "react-bootstrap";
import PhonePicker from "@services/ui-components/src/PhonePicker/PhonePicker";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import { buildInitialValuesByModules, buildValidationSchemaByModules } from "./constants";
import {
    ReactSelect,
    TableHeader,
    ReactAsyncSelect,
    Spinner,
    SpinnerLoader,
    InfoBanner,
    WithRoleAccess,
} from "@services/ui-components";
import { ApolloError, useReactiveVar } from "@apollo/client";
import BranchConfirmDelete from "../Branch/BranchConfirmDelete";
import BranchModalDelete from "../Branch/BranchModalDelete";
import BranchModalLimit from "../Branch/BranchModalLimit";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { processImages } from "../DropPhotosMultiply/drop-photos-utils";
import MultipleImageCrop from "../ui-components/MultipleImageCrop/MultipleImageCrop";
import { branchesLength, branchNameState, isRefetchBranches } from "@me-team/host/main/branches/state";
import {
    useDeleteBranchMutation,
    useGetBranchQuery,
    useGetCountriesQuery,
    useGetIsoCodeForBranchQuery,
    useRestoreBranchMutation,
    useUpdateBranchMutation,
} from "@me-team/host/main/branches/graphql/branches.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { AllModulesTagName } from "../../utils/enums";
import { BranchInput, CompanyAccess } from "@me-team/host/main/graphql/types";
import { handlePaste } from "@me-team/host/src/utils/utils";
import { ValidationErrorExtensions } from "@me-team/host/src/utils/type";
import { formatWhatsAppPath } from "@me-team/common/src/hooks/helpers";
import {
    mapWeekValueToWorkSchedulesInput,
    WORK_SCHEDULE_TYPE_GENERAL,
    WORK_SCHEDULE_TYPE_KITCHEN,
} from "../Me-menu/WorkingHours/helpers/workingHoursHelpers";
import { WorkSchedulesSection } from "../index";
import { BranchFormValues } from "./types";
import CurrentBranchInfoTooltip from "./CurrentBranchInfoTooltip";
import CustomCreatableReactSelect from "../../../../../services/ui-components/src/select/CustomCreatableReactSelect";
import { useBranchTypes } from "../../hooks/useBranchTypes";
import { useModulesAccess } from "@me-team/host/src/utils/useModulesAccess";
import MenuLanguageVersionSelectContainer from "../Me-menu/components/LanguageVersionSelect/MenuLanguageVersionSelectContainer";
import { useCountryCitiesPaginatedOptions } from "../../hooks/useCountryCitiesPaginatedOptions";
import { CUSTOM_CITY_OPTION_VALUE } from "../../constants/city";
import { getCitySelectValue } from "../CitySelect/helpers/getCitySelectValue";
import { Option } from "@me-team/common/src/interfaces/interfaces";
import { useRoleAccess, useRoleAccessAny } from "@me-team/host/src/utils/useRoleAccess";

const CurrentBranch: React.FC = () => {
    const { t } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const basePath = process.env.REACT_APP_POS_URL;
    const navigate = useNavigate();
    const { updateId, branchId } = useParams();
    const [photos, setPhotos] = useState([]);
    const [initialValues, setInitialValues] = useState<BranchFormValues | null>(null);
    const [countryId, setCountryId] = useState<number | null>(null);
    const [showCustomCityInput, setShowCustomCityInput] = useState<boolean>(false);
    const [currentSelectedCityOption, setCurrentSelectedCityOption] = useState<Option | null>(null);
    const [isRestoring, setIsRestoring] = useState<boolean>(false);
    const [branchIdToDelete, setBranchIdToDelete] = useState<number | null>(null);
    const [showModalConfirmDeleteBranch, setShowModalConfirmDeleteBranch] = useState<boolean>(false);
    const [showModalDeleteBranch, setShowModalDeleteBranch] = useState<boolean>(false);
    const [showModalLimit, setShowModalLimit] = useState<boolean>(false);
    const [isUpdate, setIsUpdate] = useState<boolean>(false);
    const [isUpdateGallery, setIsUpdateGallery] = useState<boolean>(false);
    const totalActiveBranches = useReactiveVar(branchesLength);
    const enabledOnlineBookingModule: boolean = useModulesAccess([AllModulesTagName.BOOKING]);
    const enabledMenuModule: boolean = useModulesAccess([AllModulesTagName.MENU]);
    const canViewBranch = useRoleAccessAny([CompanyAccess.BranchView, CompanyAccess.BranchEdit]);
    const canEditBranch = useRoleAccess(CompanyAccess.BranchEdit);
    const canDeleteRestoreBranch = useRoleAccess(CompanyAccess.BranchDeleteRestore);
    const canSubmitBranch = isRestoring ? canDeleteRestoreBranch : canEditBranch;
    const isBranchReadOnly = !canSubmitBranch;
    // TODO Временно скрываем селект языковой версии, уберем скрытие, когда реализуем языковые
    //  версии для филиала, то есть добавление переводов для инпутов реадктирования филиала
    const showMenuLanguageVersionSelect = false;
    const hasServiceExtraFields = enabledOnlineBookingModule;
    const hasFoodExtraFields = enabledMenuModule;

    const infoTooltipText = useMemo(() => {
        if (enabledOnlineBookingModule) {
            return t("This data will be displayed in the online booking form");
        }
        if (enabledMenuModule) {
            return t("This data is displayed in the qr-menu");
        }
        return null;
    }, [enabledOnlineBookingModule, enabledMenuModule, t]);

    const { data: branchTypesData, loading: branchTypesLoading } = useBranchTypes();
    const businessTypes = branchTypesData?.branchTypes || null;

    const { data: branchData, loading } = useGetBranchQuery({
        variables: { id: Number(updateId), companyId: +currentCompanyId },
        skip: !currentCompanyId || !updateId,
        onCompleted: async (data) => {
            const images = data?.user?.company?.branchesPaginated?.branches[0]?.branchImages;
            const files = await processImages(images, basePath);
            setPhotos(files);
        },
        context: {
            errorType: "global",
        },
        onError: (error) => {
            console.error("error", error);
        },
    });

    const branch = useMemo(() => branchData?.user?.company?.branchesPaginated?.branches[0], [branchData]);
    const timeZone = branchData?.user?.company?.timeZone?.name;

    const validationSchema = useMemo(
        () =>
            buildValidationSchemaByModules(t, {
                hasServiceExtraFields,
            }),
        [hasServiceExtraFields, t]
    );

    const { data: isoCodeData, loading: isoCodeLoading } = useGetIsoCodeForBranchQuery({
        variables: { companyId: Number(currentCompanyId) },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            console.error("error", error);
        },
    });
    const phoneCode = isoCodeData?.user?.company?.country?.isoCode.toLowerCase();
    const branchName = useReactiveVar(branchNameState);
    useDocumentTitle(`${t("Branch editing")} ${branchName}`);
    const { data: countriesData } = useGetCountriesQuery();

    const [deleteBranch] = useDeleteBranchMutation();
    const [updateBranch] = useUpdateBranchMutation();
    const [restoreBranch] = useRestoreBranchMutation();

    const countryOptions =
        countriesData?.relations.countries.map((country) => ({
            label: country.name,
            value: country.id,
        })) || [];

    const {
        cityOptions,
        selectedCityOption,
        isCitiesInitialLoading,
        isCitiesSearchLoading,
        onCityMenuOpen,
        onCityMenuClose,
        onCitySearchChange,
        onCityMenuScrollToBottom,
        resetCities,
    } = useCountryCitiesPaginatedOptions({
        countryId,
        selectedCity:
            branch?.country?.id && Number(branch.country.id) === countryId && branch?.city
                ? {
                      id: branch.city.id,
                      name: branch.city.name,
                  }
                : null,
        includeCustomCityOption: true,
        customCityOptionLabel: t("Other city"),
        skip: !countryId,
    });

    useEffect(() => {
        if (loading || !branchData || !branchData.user?.company?.branchesPaginated?.branches[0]) return;

        const currentBranch = branchData.user.company.branchesPaginated.branches[0];
        setCountryId(+currentBranch.country.id);
        branchNameState(currentBranch.name);
        setIsRestoring(!!currentBranch.deleted);

        setInitialValues(
            buildInitialValuesByModules(currentBranch, {
                hasServiceExtraFields,
                hasFoodExtraFields,
            })
        );
    }, [branchData, loading, hasServiceExtraFields, hasFoodExtraFields]);

    const handleSubmit = async (
        values: BranchFormValues,
        {
            setSubmitting,
            setFieldError,
        }: {
            setSubmitting: (isSubmitting: boolean) => void;
            setFieldError: (field: string, message: string) => void;
        },
        isRestoring: boolean = false
    ) => {
        const selectedCityId = values.city ? Number(values.city) : null;

        if (isRestoring ? !canDeleteRestoreBranch : !canEditBranch) {
            setSubmitting(false);
            return;
        }

        const input: BranchInput = {
            category: values.category,
            type: values.type,
            name: values.name,
            country: +values.country,
            city: showCustomCityInput ? null : selectedCityId,
            otherCity: values?.customCity ? values?.customCity : null,
            address: values.address,
            phones: values.phones.map((phone: any) => ({ phone: phone.phone })),
            website: values.website,
            description: values.description ? values.description : null,
            images: photos,
            logo: "logo" in values ? values.logo : null,
            isUpdateLogo: values?.logo === null && values.image === null ? true : isUpdate,
            isUpdateGallery: isUpdateGallery,
            socialLink: {
                instagram: values.instagram,
                facebook: values.facebook,
                youtube: values.youtube,
                whatsapp: values.whatsapp,
            },
        };
        const validPhotos = photos.filter((photo) => photo != null);
        input.images = validPhotos;

        if (hasServiceExtraFields) {
            input.workSchedule = values.workSchedule || "";
        }

        if (hasFoodExtraFields) {
            const generalWorkSchedules = mapWeekValueToWorkSchedulesInput(
                values.generalWorkingHours ?? null,
                WORK_SCHEDULE_TYPE_GENERAL
            );

            const kitchenWorkSchedules = mapWeekValueToWorkSchedulesInput(
                values.kitchenWorkingHours ?? null,
                WORK_SCHEDULE_TYPE_KITCHEN
            );

            input.workSchedules = [...generalWorkSchedules, ...kitchenWorkSchedules];
        }

        try {
            if (isRestoring) {
                const existingBranches = branchData?.user.company.branchesPaginated.branches || [];
                const duplicateBranch = existingBranches.find(
                    (branch) => branch.name === values.name && !branch.deleted
                );

                if (duplicateBranch) {
                    setFieldError("name", t("A branch with this name already exists."));
                    setSubmitting(false);
                    return;
                }
            }

            const mutateBranch = isRestoring ? restoreBranch : updateBranch;

            await mutateBranch({
                variables: {
                    id: +updateId!,
                    input,
                },
                onCompleted: async () => {
                    navigate(`/branches/${branchId}`, {
                        state: { text: isRestoring ? t("Restored") : t("Saved"), toast: true },
                    });
                },
            });
            isRefetchBranches(true);
        } catch (error) {
            if (error?.graphQLErrors?.[0]?.extensions?.category === "branch.already_exists") {
                setFieldError("name", t("Branch with this name already exists!"));
                return;
            }
            if (error?.graphQLErrors?.[0]?.extensions?.category === "branch.tariff_error") {
                setShowModalLimit(true);
                isRefetchBranches(true);
            }
            const validationErrors = (error?.graphQLErrors?.[0]?.extensions as ValidationErrorExtensions | undefined)
                ?.validation;
            const websiteErrors = validationErrors?.website;
            if (Array.isArray(websiteErrors)) {
                const longError = websiteErrors.find((e) =>
                    e.message.includes("This value is too long. It should have 2040 characters or less.")
                );
                if (longError) {
                    setFieldError("website", t("The value length for this field must not exceed 2040 characters."));
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenConfirmModalBranch = (branchId: number) => {
        if (!canDeleteRestoreBranch) return;

        setBranchIdToDelete(branchId);
        setShowModalConfirmDeleteBranch(true);
        isRefetchBranches(true);
    };

    const handleCloseConfirmModalBranch = () => {
        setBranchIdToDelete(null);
        setShowModalConfirmDeleteBranch(false);
    };

    const handleCloseModalBranch = () => {
        setShowModalDeleteBranch(false);
    };

    const handleCloseModalLimit = () => {
        setShowModalLimit(false);
    };

    const handleDeleteBranch = async () => {
        if (!canDeleteRestoreBranch) return;

        if (branchIdToDelete !== null) {
            try {
                await deleteBranch({
                    variables: { id: branchIdToDelete },
                });
                navigate(`/branches/${branchId}`, { state: { text: t("Deleted"), toast: true } });
            } catch (error) {
                console.error("Error deleting branch:", error);
                if (error?.graphQLErrors?.[0]?.extensions?.category === "branch.latest") {
                    setShowModalDeleteBranch(true);
                }
            } finally {
                handleCloseConfirmModalBranch();
                isRefetchBranches(true);
            }
        }
    };
    const handlePhoto = () => {
        setIsUpdate(true);
    };
    const setFieldConfirmation = () => {
        setIsUpdateGallery(true);
    };
    if (loading || isoCodeLoading || branchTypesLoading) return <Spinner />;
    if (!initialValues) return <div>No data found</div>;
    if (!canViewBranch && !canDeleteRestoreBranch) return null;

    return (
        <div className="px-lg-6">
            <InfoBanner positionIcon="top">
                <p className="fs-7 mb-0">
                    {t(
                        "On this page, you can specify information about your branch. Required" +
                            " fields are marked with an asterisk - *."
                    )}
                </p>
                <p className="fs-7 mb-0">
                    {t("The data that customers see in the online record form is marked")}
                    <i className="bi bi-info-circle ms-1"></i>.
                </p>
            </InfoBanner>
            <TableHeader
                colSize={4}
                btnColSize={8}
                title={isRestoring ? t("Restoring branch") : t("Branch")}
                buttons={
                    <Row className="d-flex gy-sm-0 align-items-start  mt-3 mt-lg-0 justify-content-md-start justify-content-lg-end">
                        {enabledMenuModule && showMenuLanguageVersionSelect ? (
                            <Col xs={12} md={6} lg={5} xxl={4}>
                                <div className="position-relative">
                                    <span className="position-absolute start-0 top-0 z-1 ps-6 pt-1 fs-8 text-grey">
                                        {t("Language version")}
                                    </span>
                                    <MenuLanguageVersionSelectContainer
                                        companyId={Number(currentCompanyId)}
                                        fetchPolicy="cache-first"
                                    />
                                </div>
                            </Col>
                        ) : null}
                        {!isRestoring ? (
                            <WithRoleAccess permission={CompanyAccess.BranchDeleteRestore}>
                                <Col
                                    xs={12}
                                    md={6}
                                    lg={5}
                                    xxl={4}
                                    className={`${enabledMenuModule && showMenuLanguageVersionSelect ? "" : "ms-md-auto"} mt-3 mt-md-0`}
                                >
                                    <Button
                                        disabled={totalActiveBranches === 1 || !canDeleteRestoreBranch}
                                        variant="outline-orange"
                                        className="fw-normal px-3 rounded-1 text-capitalize w-100"
                                        onClick={() =>
                                            handleOpenConfirmModalBranch(
                                                branchData?.user?.company?.branchesPaginated?.branches?.[0]?.id
                                            )
                                        }
                                    >
                                        {t("Delete branch")}
                                    </Button>
                                </Col>
                            </WithRoleAccess>
                        ) : null}
                    </Row>
                }
            />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => handleSubmit(values, actions, isRestoring)}
                enableReinitialize
            >
                {({ setFieldValue, values, isSubmitting, errors, touched }) => {
                    const handleCityChange = (selectedOption: SelectOption | null) => {
                        if (selectedOption && selectedOption.value === (CUSTOM_CITY_OPTION_VALUE as any)) {
                            setShowCustomCityInput(true);
                            setFieldValue("city", null);
                            setCurrentSelectedCityOption(null);
                        } else {
                            setShowCustomCityInput(false);
                            setFieldValue("city", selectedOption ? selectedOption.value : null);
                            setFieldValue("customCity", null);
                            setCurrentSelectedCityOption(
                                selectedOption
                                    ? { value: Number(selectedOption.value), label: selectedOption.label }
                                    : null
                            );
                        }
                    };

                    const cityFieldValue = getCitySelectValue({
                        showCustomCityInput,
                        cityOptions,
                        cityValue: values.city,
                        currentSelectedCityOption,
                        selectedCityOption,
                    });
                    values.whatsapp = formatWhatsAppPath(values.whatsapp);

                    return (
                        <Form>
                            <Row className="pt-3">
                                <Col lg={9} className="pe-lg-5">
                                    <h5 className="fs-4 text-capitalize mb-4">{t("Basic information")}</h5>
                                    <div className="pe-lg-4">
                                        <div className="mb-3 mb-sm-4">
                                            <Row className="align-items-center">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="currentBranch-name-field"
                                                        className="fs-7 fw-bold required-label"
                                                    >
                                                        {t("Establishment name (branch)")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <div className="position-relative">
                                                        <Field
                                                            id={"currentBranch-name-field"}
                                                            disabled={isBranchReadOnly}
                                                            name="name"
                                                            placeholder={t("Branch name")}
                                                            maxLength={50}
                                                            className={`form-control rounded-1 lh-22 pe-5 ${errors.name && touched.name ? "border-danger" : "border-secondary"}`}
                                                        />
                                                        <CurrentBranchInfoTooltip
                                                            text={infoTooltipText}
                                                            containerClassName="pt-1 position-absolute top-50 translate-middle-y end-0 me-3"
                                                        />
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Col sm={9} md={10} className="ms-auto">
                                                <ErrorMessage name="name">
                                                    {(msg) => <p className="fs-7 py-1 px-2 text-danger m-0">{msg}</p>}
                                                </ErrorMessage>
                                            </Col>
                                        </div>

                                        <Row className="align-items-center mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="currentBranch-category-field"
                                                    className="fs-7 fw-bold required-label"
                                                >
                                                    {t("Business type")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <Field
                                                    id={"currentBranch-category-field"}
                                                    disabled
                                                    name="category"
                                                    placeholder={t("Business type")}
                                                    value={branch?.category?.name}
                                                    maxLength={50}
                                                    className={`form-control rounded-1 lh-22 pe-5 ${errors.name && touched.name ? "border-danger" : "border-secondary"}`}
                                                />
                                            </Col>
                                        </Row>
                                        {hasFoodExtraFields && (
                                            <>
                                                <Row className="mb-3 mb-sm-4">
                                                    <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="currentBranch-name-field"
                                                                    className="mb-0 text-dark form-label text-break"
                                                                >
                                                                    {t("Type of Establishment")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </Col>
                                                    <Col sm={9} md={10}>
                                                        <div className="position-relative">
                                                            <CustomCreatableReactSelect
                                                                optionsArray={businessTypes}
                                                                placeholder={t("Add type")}
                                                                setField={setFieldValue}
                                                                fieldName="type"
                                                                value="name"
                                                                isTooltip={true}
                                                                name={"type"}
                                                                id={"currentBranch-name-field"}
                                                                maxLength={50}
                                                                createOptionLabel={t("Create the name of type")}
                                                                noOptionsText={t("Start typing the name")}
                                                                createOnBlur={true}
                                                                disabled={isBranchReadOnly}
                                                            />

                                                            <CurrentBranchInfoTooltip
                                                                text={infoTooltipText}
                                                                containerClassName="position-absolute top-50 end-0 translate-middle-y pe-6"
                                                                containerStyle={{ height: "16px" }}
                                                                iconClassName="bi bi-info-circle position-absolute top-50 translate-middle-y end-0 me-3"
                                                            />
                                                        </div>
                                                    </Col>
                                                    <div className="col-sm-9 col-md-10 ms-auto">
                                                        <span className="text-grey fs-7">
                                                            {" "}
                                                            {values.type?.length}/50
                                                        </span>
                                                        <ErrorMessage name="type">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </Row>

                                                <Row className="mb-3 mb-sm-4">
                                                    <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="currentBranch-description-field"
                                                            className="fs-7 fw-bold pt-2"
                                                        >
                                                            {t("Description of the establishment")}
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} md={10}>
                                                        <div className="position-relative">
                                                            <Field
                                                                id="currentBranch-description-field"
                                                                as="textarea"
                                                                name="description"
                                                                placeholder={t("Add Description")}
                                                                maxLength={600}
                                                                disabled={isBranchReadOnly}
                                                                className="form-control pe-5 rounded-1"
                                                            />
                                                            <CurrentBranchInfoTooltip
                                                                text={infoTooltipText}
                                                                containerClassName="pt-1 position-absolute top-0 end-0 mt-1 me-3"
                                                            />
                                                        </div>
                                                        <ErrorMessage name="description">
                                                            {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                        </ErrorMessage>
                                                        <div className="fs-7 text-grey text-start mt-1 px-1">
                                                            {`${values.description.length}/600`}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="currentBranch-country-field"
                                                    className="fs-7 fw-bold mt-sm-2 pt-1 required-label"
                                                >
                                                    {t("country")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <ReactSelect
                                                    id={"currentBranch-country-field"}
                                                    name="country"
                                                    inputAutoComplete="new-password"
                                                    disabled={isBranchReadOnly}
                                                    options={countryOptions}
                                                    value={countryOptions.find(
                                                        (option: any) => option.value === values.country
                                                    )}
                                                    onChange={(selectedOption: SelectOption | null) => {
                                                        setFieldValue(
                                                            "country",
                                                            selectedOption ? selectedOption.value : ""
                                                        );
                                                        setCountryId(
                                                            selectedOption ? parseInt(selectedOption.value, 10) : null
                                                        );
                                                        setFieldValue("city", null);
                                                        setFieldValue("customCity", null);
                                                        setShowCustomCityInput(false);
                                                        resetCities();
                                                    }}
                                                    placeholder={t("Select Country")}
                                                    isSearchable
                                                />
                                                <ErrorMessage name="country">
                                                    {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                </ErrorMessage>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="type"
                                                    className="fs-7 fw-bold mt-sm-2 pt-1 required-label"
                                                >
                                                    {t("city")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <ReactAsyncSelect
                                                    id={"currentBranch-city-field"}
                                                    key={`city-${values.country}`}
                                                    name="city"
                                                    inputAutoComplete="new-password"
                                                    disabled={isBranchReadOnly}
                                                    options={cityOptions}
                                                    value={cityFieldValue}
                                                    onChange={handleCityChange}
                                                    placeholder={t("Select City")}
                                                    isSearchable
                                                    isLoading={isCitiesInitialLoading || isCitiesSearchLoading}
                                                    onMenuOpen={onCityMenuOpen}
                                                    onMenuClose={onCityMenuClose}
                                                    onInputChange={onCitySearchChange}
                                                    onMenuScrollToBottom={onCityMenuScrollToBottom}
                                                    disableLocalFilter
                                                    noOptionsMessage={() => t("No options")}
                                                    loadingMessage={() => t("Loading...")}
                                                />
                                                <ErrorMessage name="city">
                                                    {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                </ErrorMessage>
                                            </Col>
                                        </Row>

                                        {showCustomCityInput && (
                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="currentBranch-customCity-field"
                                                        className="fs-7 fw-bold pt-2 required-label"
                                                    >
                                                        {t("Add custom city name")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <Field
                                                        id={"currentBranch-customCity-field"}
                                                        name="customCity"
                                                        autoComplete="new-password"
                                                        placeholder={t("Enter custom city name")}
                                                        className={`form-control ${errors.customCity && touched.customCity ? "border-danger" : "border-secondary"}`}
                                                        onChange={(e: any) => {
                                                            setFieldValue("customCity", e.target.value);
                                                        }}
                                                        maxLength={50}
                                                    />
                                                    <ErrorMessage name="customCity">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>
                                        )}

                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="currentBranch-address-field"
                                                    className="fs-7 fw-bold mt-sm-2 pt-1 required-label"
                                                >
                                                    {t("address")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <div className="position-relative">
                                                    <Field
                                                        id={"currentBranch-address-field"}
                                                        disabled={isBranchReadOnly}
                                                        name="address"
                                                        autoComplete="address-line1"
                                                        placeholder={t("Enter your location")}
                                                        className={`form-control pe-5 ${errors.address && touched.address ? "border-danger" : "border-secondary"}`}
                                                        maxLength={200}
                                                    />
                                                    <CurrentBranchInfoTooltip
                                                        text={infoTooltipText}
                                                        containerClassName="pt-1 position-absolute top-50 translate-middle-y end-0 me-3"
                                                    />
                                                </div>
                                                <ErrorMessage name="address">
                                                    {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                </ErrorMessage>
                                            </Col>
                                        </Row>

                                        <div className="form-group">
                                            <Row className="mb-2">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label className="fs-7 fw-bold mt-sm-2 pt-1 required-label">
                                                        {t("Phone")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <FieldArray
                                                        name="phones"
                                                        render={({ remove, push }) => (
                                                            <>
                                                                {values.phones.map((phone: any, index: number) => {
                                                                    return (
                                                                        <div
                                                                            className="d-flex align-items-center w-100 mb-3"
                                                                            key={index}
                                                                        >
                                                                            <div className="w-100">
                                                                                <PhonePicker
                                                                                    id={`currentBranch-phones.${index}.phone-field`}
                                                                                    disabled={isBranchReadOnly}
                                                                                    key={`phones.${index}.phone`}
                                                                                    name={`phones.${index}.phone`}
                                                                                    value={phone.phone}
                                                                                    defaultCountry={phoneCode}
                                                                                />
                                                                                <ErrorMessage
                                                                                    name={`phones.${index}.phone`}
                                                                                >
                                                                                    {(msg) => (
                                                                                        <p className="fs-7 p-1 text-danger m-0">
                                                                                            {msg}
                                                                                        </p>
                                                                                    )}
                                                                                </ErrorMessage>
                                                                            </div>
                                                                            {index !== 0 && (
                                                                                <Button
                                                                                    type="button"
                                                                                    variant="outline-secondary"
                                                                                    className="h-100 ms-3 px-3 py-6 fs-normal rounded-1 align-self-start"
                                                                                    onClick={() => remove(index)}
                                                                                    disabled={
                                                                                        isBranchReadOnly || isSubmitting
                                                                                    }
                                                                                >
                                                                                    <i className="bi bi-trash3 text-dark"></i>
                                                                                </Button>
                                                                            )}

                                                                            {index === 0 && (
                                                                                <Button
                                                                                    type="button"
                                                                                    variant="outline-secondary"
                                                                                    className="h-100 ms-3 px-3 py-6 fs-normal rounded-1 align-self-start"
                                                                                    onClick={() => push("")}
                                                                                    disabled={
                                                                                        isBranchReadOnly || isSubmitting
                                                                                    }
                                                                                >
                                                                                    <i className="bi bi-plus-lg text-dark"></i>
                                                                                </Button>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </>
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                        </div>

                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="currentBranch-website-field"
                                                    className="fs-7 fw-bold mt-sm-2 pt-1"
                                                >
                                                    {t("website")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <Field
                                                    id={"currentBranch-website-field"}
                                                    disabled={isBranchReadOnly}
                                                    name="website"
                                                    placeholder={t("Site address")}
                                                    className={`form-control rounded-1 lh-22 pe-5 ${errors.website && touched.website ? "border-danger" : "border-secondary"}`}
                                                    maxLength={2040}
                                                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                                                        handlePaste(e, 2040)
                                                    }
                                                />
                                                <ErrorMessage name="website">
                                                    {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                </ErrorMessage>
                                            </Col>
                                        </Row>
                                        {hasServiceExtraFields ? (
                                            <div className="mb-3 mb-sm-4">
                                                <Row className="align-items-center">
                                                    <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="currentBranch-workSchedule-field"
                                                            className="fs-7 fw-bold required-label"
                                                        >
                                                            {t("Work schedule")}
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} md={10}>
                                                        <div className="position-relative">
                                                            <Field
                                                                id={"currentBranch-workSchedule-field"}
                                                                disabled={isBranchReadOnly}
                                                                name="workSchedule"
                                                                placeholder={t("Work schedule")}
                                                                maxLength={50}
                                                                className={`form-control rounded-1 lh-22 pe-5 ${errors.workSchedule && touched.workSchedule ? "border-danger" : "border-secondary"}`}
                                                            />
                                                            <CurrentBranchInfoTooltip
                                                                text={infoTooltipText}
                                                                containerClassName="pt-1 position-absolute top-50 translate-middle-y end-0 me-3"
                                                            />
                                                        </div>
                                                    </Col>
                                                </Row>
                                                <Col sm={9} md={10} className="ms-auto">
                                                    <ErrorMessage name="workSchedule">
                                                        {(msg) => (
                                                            <p className="fs-7 py-1 px-2 text-danger m-0">{msg}</p>
                                                        )}
                                                    </ErrorMessage>
                                                    <div className="mt-1 px-2">
                                                        <small className="text-grey">
                                                            {t("for example, Mon-Sun : 11:00-22:00")}
                                                        </small>
                                                    </div>
                                                </Col>
                                            </div>
                                        ) : null}
                                        {(enabledOnlineBookingModule && !enabledMenuModule) ||
                                        (!enabledOnlineBookingModule && !enabledMenuModule) ? (
                                            <Row className="mb-3 mb-sm-4">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="currentBranch-description-field"
                                                        className="fs-7 fw-bold pt-2"
                                                    >
                                                        {t("Description")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <div className="position-relative">
                                                        <Field
                                                            id={"currentBranch-description-field"}
                                                            disabled={isBranchReadOnly}
                                                            as="textarea"
                                                            name="description"
                                                            placeholder={t("Description branch")}
                                                            maxLength={200}
                                                            className="form-control pe-5 rounded-1"
                                                        />
                                                        <CurrentBranchInfoTooltip
                                                            text={infoTooltipText}
                                                            containerClassName="pt-1 position-absolute top-0 end-0 mt-1 me-3"
                                                        />
                                                    </div>
                                                    <ErrorMessage name="description">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                    <div className="fs-7 text-grey text-start mt-1 px-1">
                                                        {`${values.description.length}/200`}
                                                    </div>
                                                </Col>
                                            </Row>
                                        ) : null}
                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label htmlFor="description" className="fs-7 fw-bold">
                                                    {t("Photo of the branch")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <div className="mb-2">
                                                    <p className="fs-7 mb-0">
                                                        {t("Add jpeg images of the branch premises, interior, etc.")}
                                                        <CurrentBranchInfoTooltip
                                                            text={infoTooltipText}
                                                            iconClassName="bi bi-info-circle ms-1 fs-7 pt-1"
                                                        />
                                                    </p>
                                                </div>
                                                <Row className="gx-0">
                                                    <MultipleImageCrop
                                                        key={photos[0]}
                                                        photosArray={photos}
                                                        setPhotos={setPhotos}
                                                        setFieldValue={setFieldValue}
                                                        setConfiramitoinField={() => setFieldConfirmation()}
                                                        disabled={isBranchReadOnly}
                                                    />
                                                </Row>
                                                <div className="mt-2">
                                                    <p className="fs-7 text-grey">{t("max-size")}</p>
                                                </div>
                                            </Col>
                                        </Row>

                                        <div className="d-flex gap-2 mb-4 pt-sm-6">
                                            <h5 className="fs-4 text-capitalize">{t("Social Media")}</h5>
                                            <CurrentBranchInfoTooltip text={infoTooltipText} />
                                        </div>

                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="currentBeautySalonBranch-instagram-field"
                                                    className="fs-7 fw-bold pt-2"
                                                >
                                                    {t("Instagram")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <Field
                                                    name="instagram"
                                                    id="currentBeautySalonBranch-instagram-field"
                                                    placeholder={t("Add link")}
                                                    className="form-control"
                                                    disabled={isBranchReadOnly}
                                                />
                                                <ErrorMessage
                                                    name="instagram"
                                                    component="div"
                                                    className="invalid-feedback"
                                                />
                                            </Col>
                                        </Row>

                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="currentBeautySalonBranch-facebook-field"
                                                    className="fs-7 fw-bold pt-2"
                                                >
                                                    {t("Facebook")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <Field
                                                    name="facebook"
                                                    id="currentBeautySalonBranch-facebook-field"
                                                    placeholder={t("Add link")}
                                                    className="form-control"
                                                    disabled={isBranchReadOnly}
                                                />
                                                <ErrorMessage
                                                    name="facebook"
                                                    component="div"
                                                    className="invalid-feedback"
                                                />
                                            </Col>
                                        </Row>

                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="currentBeautySalonBranch-youtube-field"
                                                    className="fs-7 fw-bold pt-2"
                                                >
                                                    {t("Youtube")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <Field
                                                    name="youtube"
                                                    id="currentBeautySalonBranch-youtube-field"
                                                    placeholder={t("Add link")}
                                                    className="form-control"
                                                    disabled={isBranchReadOnly}
                                                />
                                                <ErrorMessage
                                                    name="youtube"
                                                    component="div"
                                                    className="invalid-feedback"
                                                />
                                            </Col>
                                        </Row>

                                        <Row className="mb-3 mb-sm-4 pb-sm-6">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="currentBeautySalonBranch-whatsapp-field"
                                                    className="fs-7 fw-bold pt-2"
                                                >
                                                    {t("WhatsApp")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <Field
                                                    name="whatsapp"
                                                    id="currentBeautySalonBranch-whatsapp-field"
                                                    placeholder={t("Add link")}
                                                    className="form-control"
                                                    disabled={isBranchReadOnly}
                                                />
                                                <ErrorMessage
                                                    name="whatsapp"
                                                    component="div"
                                                    className="invalid-feedback"
                                                />
                                            </Col>
                                        </Row>
                                        {hasFoodExtraFields && !!timeZone && (
                                            <WorkSchedulesSection
                                                title="WorkingHours.Work Schedule"
                                                timeZone={timeZone}
                                                values={values}
                                                errors={errors}
                                                touched={touched}
                                                setFieldValue={setFieldValue}
                                                fieldNames={{
                                                    general: "generalWorkingHours",
                                                    kitchen: "kitchenWorkingHours",
                                                }}
                                                disabled={isBranchReadOnly}
                                            />
                                        )}
                                    </div>
                                </Col>
                                <Col lg={3} className="ms-auto ps-lg-0">
                                    <h5 className="fs-4 mb-4">{t("logo")}</h5>
                                    <ImageCrop
                                        key={initialValues?.image}
                                        previewPhoto={initialValues?.image}
                                        setFieldValue={setFieldValue}
                                        fieldSetter={() => handlePhoto()}
                                        fieldName="logo"
                                        modalTitle={t("Branch logo")}
                                        disabled={isBranchReadOnly}
                                        dismissImage={() => setFieldValue("image", null)}
                                    />
                                </Col>
                            </Row>
                            <div className="d-sm-flex my-4 mt-lg-0">
                                <Button
                                    id={"currentBranch-cancel-button"}
                                    onClick={() => navigate(-1)}
                                    variant="outline-primary"
                                    className="fw-normal mb-3 mb-sm-0 me-3 custom-btn rounded-1"
                                >
                                    {t("Cancel")}
                                </Button>
                                <Button
                                    id={"currentBranch-submit-button"}
                                    type="submit"
                                    variant="primary"
                                    disabled={!canSubmitBranch || isSubmitting}
                                    className="fw-bold custom-btn rounded-1"
                                >
                                    {isSubmitting ? (
                                        isRestoring ? (
                                            <SpinnerLoader variant="light" loadingText={t("Restoring...")} />
                                        ) : (
                                            <SpinnerLoader variant="light" loadingText={t("Saving...")} />
                                        )
                                    ) : isRestoring ? (
                                        t("Restore")
                                    ) : (
                                        t("Save")
                                    )}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
            <BranchModalDelete
                show={showModalDeleteBranch && canDeleteRestoreBranch}
                handleClose={handleCloseModalBranch}
            />
            <BranchConfirmDelete
                show={showModalConfirmDeleteBranch && canDeleteRestoreBranch}
                handleClose={handleCloseConfirmModalBranch}
                handleDelete={handleDeleteBranch}
            />
            <BranchModalLimit show={showModalLimit} handleClose={handleCloseModalLimit} isRestore />
        </div>
    );
};

export default CurrentBranch;
